(() => {
  if (customElements.get('poiliva-reviews')) return;
  const PAGE_SIZE = 30;
  class PoilivaReviews extends HTMLElement {
    connectedCallback() {
      this.teardown();
      const config = this.querySelector('[data-reviews-config]');
      if (!config) return;
      try { this.config = JSON.parse(config.textContent); } catch { return; }
      this.controller = new AbortController();
      this.requests = new Map();
      this.records = new Map();
      this.nativeData = undefined;
      this.manifest = undefined;
      this.index = undefined;
      this.importMedia = [];
      this.previewPending = undefined;
      this.listGeneration = 0;
      const signal = this.controller.signal;
      this.querySelectorAll('[data-reviews-enhanced]').forEach(node => { node.hidden = false; });
      this.addEventListener('click', event => this.onClick(event), {signal});
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-reviews-search]')) return;
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.renderList(true), 200);
      }, {signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-reviews-filter], [data-reviews-sort]')) this.renderList(true);
      }, {signal});
      this.addEventListener('keydown', event => {
        if (!this.querySelector('[data-reviews-photo-dialog][open]') || !this.photoMedia?.length) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stepPhoto(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, {signal});
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
        }, {signal});
        dialog.addEventListener('close', () => {
          if (dialog.hasAttribute('data-reviews-list-dialog')) this.listGeneration += 1;
          if (dialog.hasAttribute('data-reviews-photo-dialog')) {
            const image = this.querySelector('[data-reviews-photo-image]');
            image.onload = null; image.onerror = null; image.removeAttribute('src'); image.hidden = true;
          }
          if (!document.querySelector('poiliva-reviews dialog[open]')) document.documentElement.classList.remove('pv-reviews-modal-open');
          if (dialog._opener?.isConnected) dialog._opener.focus({preventScroll:true});
        }, {signal});
      });
      this.observer = new ResizeObserver(() => this.schedulePosition());
      this.querySelectorAll('[data-reviews-rail]').forEach(rail => {
        rail.addEventListener('scroll', () => this.schedulePosition(), {signal, passive:true});
        this.observer.observe(rail);
      });
      this.schedulePosition();
      if (this.config.import?.enabled) {
        if ('IntersectionObserver' in window) {
          this.intersection = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            this.intersection.disconnect(); this.loadPreview();
          }, {rootMargin:'150px'});
          this.intersection.observe(this);
        } else this.loadPreview();
      }
      if (!this.config.import?.enabled) this.loadPreview();
      if (this.querySelector('[data-reviews-form-result]')) this.show(this.querySelector('[data-reviews-form-dialog]'));
    }
    disconnectedCallback() { this.teardown(); }
    teardown() {
      this.controller?.abort();
      this.observer?.disconnect(); this.intersection?.disconnect();
      clearTimeout(this.searchTimer); cancelAnimationFrame(this.frame);
      this.listGeneration = (this.listGeneration || 0) + 1;
      const image = this.querySelector('[data-reviews-photo-image]');
      if (image) { image.onload = null; image.onerror = null; image.removeAttribute('src'); image.hidden = true; }
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      if (!document.querySelector('poiliva-reviews dialog[open]')) document.documentElement.classList.remove('pv-reviews-modal-open');
    }
    message(key, values = {}) {
      let value = this.config.messages[key] || '';
      for (const [name, text] of Object.entries(values)) value = value.replaceAll(`[${name}]`, String(text));
      return value;
    }
    show(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog._opener = opener;
      dialog.showModal(); document.documentElement.classList.add('pv-reviews-modal-open');
    }
    schedulePosition() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updatePosition());
    }
    updatePosition() {
      for (const rail of this.querySelectorAll('[data-reviews-rail]')) {
        const owner = rail.closest('[data-reviews-content]');
        if (!owner) continue;
        const end = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const cards = [...rail.children];
        const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : rail.clientWidth;
        const index = cards.length ? Math.min(cards.length, Math.round(rail.scrollLeft / Math.max(step, 1)) + 1) : 0;
        owner.querySelectorAll('[data-reviews-step]').forEach(button => {
          button.disabled = cards.length <= 1 || end <= 3 || (Number(button.dataset.reviewsStep) < 0 ? rail.scrollLeft <= 3 : rail.scrollLeft >= end - 2);
        });
        const arrows = owner.querySelector('.pv-reviews__arrows');
        if (arrows) arrows.hidden = cards.length <= 1 || end <= 3;
        const label = owner.querySelector('[data-reviews-position]');
        if (label) { label.textContent = `${index} / ${cards.length}`; label.hidden = cards.length <= 1; }
      }
    }
    safeImageUrl(value) {
      try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
    }
    async requestJson(filename, manifest = false) {
      if (!manifest && !/^[a-z0-9][a-z0-9._-]*\.json$/i.test(filename)) throw new Error('Invalid review asset');
      const base = new URL(this.config.import.url, document.baseURI);
      const url = manifest ? base : new URL(filename, base);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid review URL');
      if (!manifest && this.manifest?.datasetVersion) url.searchParams.set('v', this.manifest.datasetVersion);
      const key = url.href;
      if (this.requests.has(key)) return this.requests.get(key);
      const request = (async () => {
        const controller = new AbortController();
        const signal = this.controller.signal;
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, {once:true});
        if (signal.aborted) controller.abort();
        const timeout = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), 15000);
        try {
          const response = await fetch(url.href, {signal:controller.signal, credentials:'omit'});
          if (!response.ok) throw new Error('Review asset unavailable');
          return await response.json();
        } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
      })();
      this.requests.set(key, request);
      try { return await request; } catch (error) { this.requests.delete(key); throw error; }
    }
    async loadManifest() {
      if (this.manifest) return this.manifest;
      const controller = this.controller;
      const manifest = await this.requestJson('', true);
      if (manifest.schemaVersion !== 1 || !manifest.scopes || !manifest.totals) throw new Error('Invalid review manifest');
      if (!this.isConnected || controller !== this.controller || controller.signal.aborted) throw new DOMException('Disconnected', 'AbortError');
      this.manifest = manifest;
      return manifest;
    }
    importScope() { return this.manifest?.scopes[this.config.import.scope]; }
    eligibleImportPhoto(photo) {
      return photo && typeof photo === 'object' && ['verified_tool', 'verified_no_tool'].includes(photo.photoMatchStatus) && Array.isArray(photo.productHandles) && (this.config.import.scope === 'all' || photo.productHandles.includes(this.config.import.scope));
    }
    normalizeRecord(record) {
      if (record.moderationStatus !== 'approved' || !record.id || !Number.isInteger(record.rating) || record.rating < 1 || record.rating > 5) return null;
      if (this.config.import.scope !== 'all' && !record.mappedProductHandles?.includes(this.config.import.scope)) return null;
      const review = {
        id:String(record.id), source:'import', rating:record.rating, date:String(record.date || ''),
        author:String(record.author || ''), title:String(record.title || ''), body:String(record.bodyExcerpt || ''),
        textTruncated:record.textTruncated === true, incentivized:record.incentivized === true,
        photo_assets:[]
      };
      if (this.config.showPhotos && Array.isArray(record.media)) {
        review.photo_assets = record.media.map((photo, index) => this.eligibleImportPhoto(photo) ? ({
          filename:`${review.id}:${index}`, reviewId:review.id, author:review.author, source:'import',
          url:this.safeImageUrl(photo.url), thumbnailUrl:this.safeImageUrl(photo.thumbnailUrl),
          retouched:photo.retouched === true, width:1024, height:1365
        }) : null).filter(photo => photo?.url && photo.thumbnailUrl);
      }
      return review;
    }
    async loadShard(filename) {
      const controller = this.controller;
      const data = await this.requestJson(filename);
      if (controller !== this.controller || controller.signal.aborted) throw new DOMException('Disconnected', 'AbortError');
      if (!Array.isArray(data.records)) throw new Error('Invalid review shard');
      for (const record of data.records) {
        const review = this.normalizeRecord(record);
        if (review) this.records.set(review.id, review);
      }
    }
    async resolveRows(rows) {
      await Promise.all([...new Set(rows.filter(row => !this.records.has(String(row.id))).map(row => row.shard))].map(filename => this.loadShard(filename)));
      return rows.map(row => this.records.get(String(row.id))).filter(Boolean);
    }
    async loadIndex() {
      if (this.index) return this.index;
      const controller = this.controller;
      await this.loadManifest();
      const scope = this.importScope();
      if (!scope?.approvedCount) { this.index = []; return this.index; }
      const data = await this.requestJson(scope.searchIndex);
      if (controller !== this.controller || controller.signal.aborted) throw new DOMException('Disconnected', 'AbortError');
      if (!Array.isArray(data.records)) throw new Error('Invalid review index');
      this.index = data.records.filter(row => Array.isArray(row) && row.length >= 8).map(row => ({
        id:String(row[0]), source:'import', rating:Number(row[1]), date:String(row[2] || ''), author:String(row[3] || ''),
        title:String(row[4] || ''), body:String(row[5] || ''), shard:String(row[6]),
        photoCount:this.config.showPhotos && this.manifest.photoScopeVersion === 1 ? Number(row[7]) || 0 : 0
      }));
      return this.index;
    }
    renderPreview(reviews, scope) {
      this.querySelector('[data-reviews-rail]').replaceChildren(...reviews.map(review => this.card(review, false, true)));
      this.querySelector('[data-reviews-empty]').hidden = reviews.length > 0 || Boolean(scope?.approvedCount);
      this.querySelector('[data-reviews-footer]').hidden = !reviews.length;
      this.querySelector('[data-reviews-overview]').hidden = false;
      const total = this.loadNative().reviews.length + (Number(scope?.approvedCount) || 0);
      this.querySelector('[data-reviews-read-label]').textContent = this.message('read_more', {count:total});
      this.renderImportSummary(scope);
      this.previewMedia = reviews.flatMap(review => review.photo_assets);
      const strip = this.querySelector('[data-reviews-photos-strip]');
      if (strip) {
        const photos = this.previewMedia;
        this.previewMedia = photos;
        strip.replaceChildren(...photos.slice(0, 7).map(photo => this.photoButton(photo, 'pv-reviews__photo')));
        this.querySelector('[data-reviews-gallery]').hidden = !photos.length;
      }
      this.schedulePosition();
    }
    async loadPreview() {
      if (this.previewPending) return this.previewPending;
      const status = this.querySelector('[data-reviews-status]');
      const retry = this.querySelector('[data-reviews-retry]');
      if (!status) return;
      const native = this.loadNative().reviews;
      this.renderPreview(native.slice(0, 6), this.importScope());
      if (!this.config.import?.enabled) { status.textContent = ''; return; }
      status.textContent = this.message('loading'); retry.hidden = true;
      const controller = this.controller;
      this.previewPending = (async () => {
        try {
          await this.loadManifest();
          const scope = this.importScope();
          const rows = (scope?.preview || []).slice(0, Math.max(0, 6 - native.length));
          const imported = Number(scope?.approvedCount) ? await this.resolveRows(rows) : [];
          if (!this.isConnected || controller !== this.controller || controller.signal.aborted) return;
          this.renderPreview(native.concat(imported).slice(0, 6), scope);
          status.textContent = '';
        } catch (error) {
          if (controller.signal.aborted || controller !== this.controller) return;
          status.textContent = this.message('error'); retry.hidden = false;
        } finally { if (controller === this.controller) this.previewPending = undefined; }
      })();
      return this.previewPending;
    }
    renderImportSummary(scope) {
      const native = this.loadNative().reviews;
      const total = native.length + (Number(scope?.approvedCount) || 0);
      const histogram = {...scope?.histogram};
      native.forEach(review => { histogram[review.rating] = (Number(histogram[review.rating]) || 0) + 1; });
      const sum = [1, 2, 3, 4, 5].reduce((value, rating) => value + rating * (Number(histogram[rating]) || 0), 0);
      const average = total ? Math.round(sum / total * 100) / 100 : 0;
      this.querySelector('[data-reviews-average]').textContent = total ? new Intl.NumberFormat(this.config.language, {maximumFractionDigits:2}).format(average) : '—';
      const stars = document.createElement('span'); stars.className = 'pv-reviews__average-stars';
      stars.style.setProperty('--reviews-star-fill', `${average * 20}%`);
      stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', this.message('rating', {rating:average}));
      const empty = document.createElement('span'); empty.textContent = '☆☆☆☆☆'; empty.setAttribute('aria-hidden', 'true');
      const fill = document.createElement('span'); fill.className = 'pv-reviews__star-fill'; fill.textContent = '★★★★★'; fill.setAttribute('aria-hidden', 'true');
      stars.append(empty, fill); this.querySelector('[data-reviews-stars]').replaceChildren(stars);
      this.querySelector('[data-reviews-stars]').hidden = !total;
      const totalMessage = total === 1 && this.config.messages.imported_total_one ? 'imported_total_one' : 'imported_total';
      this.querySelector('[data-reviews-total]').textContent = this.message(totalMessage, {count:total});
      const bars = [5, 4, 3, 2, 1].map(rating => {
        const count = Number(histogram[rating]) || 0;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'pv-reviews__bar';
        button.dataset.reviewsRating = rating; button.disabled = count === 0;
        button.setAttribute('aria-label', this.message('bar_label', {rating, count}));
        const stars = document.createElement('span'); stars.className = 'pv-reviews__bar-stars'; stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating); stars.setAttribute('aria-hidden', 'true');
        const track = document.createElement('span'); track.className = 'pv-reviews__bar-track'; track.setAttribute('aria-hidden', 'true');
        const fill = document.createElement('span'); fill.style.setProperty('--reviews-bar-width', `${total ? count / total * 100 : 0}%`); track.append(fill);
        const value = document.createElement('span'); value.textContent = count; button.append(stars, track, value); return button;
      });
      this.querySelector('[data-reviews-histogram]').replaceChildren(...bars);
    }
    loadNative() {
      if (this.nativeData) return this.nativeData;
      const media = this.config.showPhotos ? this.config.photos.filter(photo => !photo.demonstration).map(photo => ({...photo, source:'native', url:this.config.media[photo.filename], thumbnailUrl:this.config.media[photo.filename].replace(/([?&])width=\d+/, '$1width=160')})) : [];
      const reviews = this.config.reviews.map(review => ({...review, source:'native', photo_assets:this.config.showPhotos ? review.photo_assets.map(filename => media.find(photo => photo.filename === filename)).filter(Boolean) : []}));
      this.nativeData = {reviews, media}; return this.nativeData;
    }
    stars(rating) {
      const stars = document.createElement('span'); stars.className = 'pv-reviews__stars';
      stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', this.message('rating', {rating}));
      stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating); return stars;
    }
    photoLabel(photo) {
      const author = photo.demonstration ? photo.caption : this.message('photo_by', {author:photo.author});
      return [author, photo.retouched ? this.message('retouched_photo') : ''].filter(Boolean).join(' · ');
    }
    photoButton(photo, className = 'pv-reviews__thumb') {
      const button = document.createElement('button'); button.type = 'button'; button.className = className;
      button.dataset.reviewPhoto = photo.filename; button.dataset.reviewSource = photo.source; button.setAttribute('aria-label', this.photoLabel(photo));
      const image = document.createElement('img'); image.src = photo.thumbnailUrl; image.alt = this.photoLabel(photo);
      image.width = 80; image.height = 80; image.loading = 'lazy'; image.decoding = 'async'; button.append(image); return button;
    }
    card(review, detail = false, preview = false) {
      const card = document.createElement('article'); card.className = preview ? 'pv-reviews__card' : 'pv-reviews__card pv-reviews__card--list';
      card.dataset.reviewSource = review.source; card.dataset.reviewId = review.id; card.dataset.rating = review.rating; card.append(this.stars(review.rating));
      const byline = document.createElement('div'); byline.className = 'pv-reviews__byline';
      const author = document.createElement('strong'); author.textContent = review.author; byline.append(author);
      card.append(byline);
      if (review.date) {
        const time = document.createElement('time'); time.dateTime = review.date;
        const date = /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(review.date) ? new Date(review.date.slice(0, 10) + 'T12:00:00Z') : null;
        time.textContent = date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(this.config.language || document.documentElement.lang, {day:'numeric', month:'short', year:'numeric'}).format(date) : review.date; card.append(time);
      }
      if (review.title) { const title = document.createElement('h3'); title.textContent = review.title; card.append(title); }
      const summary = document.createElement('p'); summary.className = 'pv-reviews__summary'; summary.textContent = review.body; card.append(summary);
      for (const key of [detail && review.textTruncated ? 'excerpt_notice' : '', review.incentivized ? 'incentivized_notice' : ''].filter(Boolean)) {
        const notice = document.createElement('p'); notice.className = 'pv-reviews__origin'; notice.textContent = this.message(key); card.append(notice);
      }
      const bottom = document.createElement('div'); bottom.className = 'pv-reviews__card-bottom';
      for (const photo of review.photo_assets) bottom.append(this.photoButton(photo));
      if (!detail) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'pv-reviews__link';
        button.dataset.reviewDetail = review.id; button.dataset.reviewSource = review.source; button.textContent = this.message('details'); bottom.append(button);
      }
      if (bottom.childElementCount) card.append(bottom); return card;
    }
    updateListOrigin() {
      this.querySelector('[data-reviews-list-title]').textContent = this.config.nativeHeading;
      const origin = this.querySelector('[data-reviews-list-origin]');
      origin.textContent = ''; origin.hidden = true;
    }
    openList(opener, rating = 'all') {
      this.querySelector('[data-reviews-filter]').value = rating;
      this.querySelector('[data-reviews-search]').value = '';
      this.querySelector('[data-reviews-sort]').value = opener?.hasAttribute('data-reviews-photos') ? 'photos' : 'source';
      this.updateListOrigin(); this.show(this.querySelector('[data-reviews-list-dialog]'), opener); this.renderList(true);
    }
    async renderList(reset) {
      const generation = ++this.listGeneration;
      if (reset) this.listPage = 0;
      const status = this.querySelector('[data-reviews-list-status]');
      const list = this.querySelector('[data-reviews-list]');
      const more = this.querySelector('[data-reviews-more]');
      const previous = this.querySelector('[data-reviews-previous-page]');
      const retry = this.querySelector('[data-reviews-list-retry]');
      list.setAttribute('aria-busy', 'true'); status.textContent = this.message('loading'); retry.hidden = true;
      more.disabled = true; previous.disabled = true;
      if (reset) list.replaceChildren();
      try {
        const native = this.loadNative().reviews;
        const imported = this.config.import?.enabled ? await this.loadIndex() : [];
        const data = native.concat(imported);
        const rating = this.querySelector('[data-reviews-filter]').value;
        const language = this.config.language || document.documentElement.lang;
        const query = this.querySelector('[data-reviews-search]').value.trim().toLocaleLowerCase(language);
        let reviews = data.filter(review => (rating === 'all' || String(review.rating) === rating) && `${review.author} ${review.title} ${review.body}`.toLocaleLowerCase(language).includes(query));
        const sort = this.querySelector('[data-reviews-sort]').value;
        if (sort === 'recent') reviews = [...reviews].sort((a,b) => b.date.localeCompare(a.date));
        if (sort === 'photos') reviews = [...reviews].sort((a,b) => (b.photoCount ?? b.photo_assets.length) - (a.photoCount ?? a.photo_assets.length));
        const start = this.listPage * PAGE_SIZE;
        const selected = reviews.slice(start, start + PAGE_SIZE);
        await this.resolveRows(selected.filter(review => review.source === 'import'));
        const shown = selected.map(review => review.source === 'import' ? this.records.get(String(review.id)) : review).filter(Boolean);
        if (generation !== this.listGeneration || !this.isConnected) return;
        list.replaceChildren(...shown.map(review => this.card(review)));
        this.listMedia = shown.flatMap(review => review.photo_assets);
        status.textContent = reviews.length ? this.message('page_results', {start:start + 1, end:start + selected.length, total:reviews.length}) : this.message('empty');
        more.hidden = start + selected.length >= reviews.length; more.disabled = false;
        more.textContent = this.message('next_page');
        previous.hidden = this.listPage === 0; previous.disabled = false;
        if ((document.activeElement === more && more.hidden) || (document.activeElement === previous && previous.hidden)) {
          const target = !more.hidden ? more : !previous.hidden ? previous : status;
          if (target === status) status.tabIndex = -1;
          target.focus({preventScroll:true});
        }
      } catch (error) {
        if (generation !== this.listGeneration || this.controller.signal.aborted) return;
        status.textContent = this.message('error'); retry.hidden = false; more.hidden = true; previous.hidden = true;
      } finally { if (generation === this.listGeneration) list.setAttribute('aria-busy', 'false'); }
    }
    async openDetail(id, opener) {
      const dialog = this.querySelector('[data-reviews-detail-dialog]'); this.show(dialog, opener);
      const content = this.querySelector('[data-reviews-detail]'); content.textContent = this.message('loading');
      const source = opener?.dataset.reviewSource || 'native';
      try {
        const review = source === 'import' ? this.records.get(id) : this.loadNative().reviews.find(item => item.id === id);
        if (!review) throw new Error('Review unavailable');
        content.replaceChildren(this.card(review, true));
        this.detailMedia = review.photo_assets;
      } catch { content.textContent = this.message('error'); }
    }
    openPhoto(filename, opener) {
      this.photoMedia = opener?.closest('[data-reviews-detail]') ? this.detailMedia : opener?.closest('[data-reviews-list]') ? this.listMedia : this.previewMedia || this.loadNative().media;
      this.photoIndex = this.photoMedia?.findIndex(photo => photo.filename === filename) ?? -1;
      this.show(this.querySelector('[data-reviews-photo-dialog]'), opener);
      if (this.photoIndex < 0) { this.querySelector('[data-reviews-photo-caption]').textContent = this.message('error'); return; }
      this.renderPhoto();
    }
    stepPhoto(direction) {
      if (!this.photoMedia?.length) return;
      this.photoIndex = Math.max(0, Math.min(this.photoMedia.length - 1, this.photoIndex + direction)); this.renderPhoto();
    }
    renderPhoto() {
      const photo = this.photoMedia?.[this.photoIndex]; if (!photo) return;
      const image = this.querySelector('[data-reviews-photo-image]');
      const caption = this.querySelector('[data-reviews-photo-caption]');
      const label = this.photoLabel(photo);
      const description = `${photo.demonstration ? this.message('demonstration') + ' · ' : ''}${label} · ${this.photoIndex + 1} / ${this.photoMedia.length}`;
      caption.textContent = `${label} · ${this.message('loading')}`; image.hidden = true;
      image.onload = () => { if (this.photoMedia?.[this.photoIndex] === photo && this.querySelector('[data-reviews-photo-dialog]').open) { image.hidden = false; caption.textContent = description; } };
      image.onerror = () => { if (this.photoMedia?.[this.photoIndex] === photo) caption.textContent = `${label} · ${this.message('error')}`; };
      image.alt = label; image.width = photo.width || 1024; image.height = photo.height || 1365; image.decoding = 'async'; image.src = photo.url;
      this.querySelectorAll('[data-reviews-photo-step]').forEach(button => { button.disabled = Number(button.dataset.reviewsPhotoStep) < 0 ? this.photoIndex === 0 : this.photoIndex === this.photoMedia.length - 1; });
    }
    onClick(event) {
      const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-reviews-close')) { button.closest('dialog').close(); return; }
      if (button.hasAttribute('data-reviews-retry')) this.loadPreview();
      if (button.hasAttribute('data-reviews-list-retry')) this.renderList(false);
      if (button.hasAttribute('data-reviews-step')) {
        const rail = button.closest('[data-reviews-content]')?.querySelector('[data-reviews-rail]');
        rail?.scrollBy({left:Number(button.dataset.reviewsStep) * rail.clientWidth, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }
      if (button.hasAttribute('data-reviews-open')) this.openList(button);
      if (button.hasAttribute('data-reviews-rating')) this.openList(button, button.dataset.reviewsRating);
      if (button.hasAttribute('data-reviews-more')) { this.listPage += 1; this.renderList(false); }
      if (button.hasAttribute('data-reviews-previous-page')) { this.listPage = Math.max(0, this.listPage - 1); this.renderList(false); }
      if (button.hasAttribute('data-review-detail')) this.openDetail(button.dataset.reviewDetail, button);
      if (button.hasAttribute('data-review-photo')) this.openPhoto(button.dataset.reviewPhoto, button);
      if (button.hasAttribute('data-reviews-photo-step')) this.stepPhoto(Number(button.dataset.reviewsPhotoStep));
      if (button.hasAttribute('data-reviews-write')) this.show(this.querySelector('[data-reviews-form-dialog]'), button);
    }
  }
  customElements.define('poiliva-reviews', PoilivaReviews);
})();
